import { useUserStore } from '@unifyapps/carbon/auth/UserStoreProvider';
import { injectMatrixThemeStyleOverride } from '@unifyapps/carbon/theme/utils/injectMatrixThemeStyleOverride';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import type { OnActionArgs, OnActionType } from '../../../../context/ActionsProvider/context';
import { useAppDependencyGraphProvider } from '../../../../context/DependencyGraphProvider';
import { useGlobalAppStateStore } from '../../../../stores/app/GlobalAppStateStore';
import InterfaceHelper from '../../../../stores/InterfaceStore/helper/InterfaceHelper';
import { InterfaceModes } from '../../../../stores/InterfaceStore/types';
import { ComputeContextKeys } from '../../../computeContext/const';
import { useGetNoCodeComputedData } from '../../../computeContext/useGetNoCodeComputedData';
import { SHOULD_NOT_PROPAGATE_UP } from '../../const';
import { useUpdateThemeForPrivateInterface } from './hooks/useUpdateThemeForPrivateInterface';
import { useUpdateThemeForPublicInterface } from './hooks/useUpdateThemeForPublicInterface';

export default function useSetTheme() {
  const getGlobalAppStateStoreState = useGlobalAppStateStore().getState;
  const { getNoCodeComputedData } = useGetNoCodeComputedData();
  const getUserStoreState = useUserStore().getState;
  const { updateThemeForPrivateInterface } = useUpdateThemeForPrivateInterface();
  const { updateThemeForPublicInterface } = useUpdateThemeForPublicInterface();

  const {
    actions: { notifyDependencyChange },
  } = useAppDependencyGraphProvider();

  const onSetTheme: OnActionType = useEventCallback((args?: OnActionArgs) => {
    const {
      actions: { setActiveTheme },
      activeTheme,
      availableThemes,
      interfaceRecord,
      mode,
    } = getGlobalAppStateStoreState();
    const { actionContext, action } = args ?? {};
    const { payload } = action ?? {};

    if (!payload?.themeId) {
      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    }

    const { action: computedAction } = getNoCodeComputedData<{ action: { themeId: string } }>(
      { action: payload },
      actionContext as Record<string, unknown>,
    );

    const themeId = computedAction.themeId;
    const isRunnerMode = mode === InterfaceModes.RUNNER;
    const isPublicInterface = InterfaceHelper.isPublicInterface(interfaceRecord);
    const isLoggedIn = getUserStoreState().isLoggedIn;

    if (!themeId || themeId === activeTheme?.id) {
      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    }

    const newTheme = availableThemes?.find((theme) => theme.id === themeId);

    if (!newTheme) {
      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    }

    setActiveTheme(newTheme);
    notifyDependencyChange({
      id: ComputeContextKeys.THEME,
    });

    if (!isPublicInterface && isRunnerMode && isLoggedIn) {
      updateThemeForPrivateInterface({ themeId, interfaceId: interfaceRecord?.id });
    } else {
      updateThemeForPublicInterface({ themeId });
    }

    if (isRunnerMode) {
      const legacyThemeOutput = interfaceRecord?.properties?.theme?.output;
      injectMatrixThemeStyleOverride({ ...newTheme, legacyThemeOutput });
    }

    return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
  });

  return {
    onSetTheme,
  };
}
